import type { LearningDomain, Subject } from '@/content/schema';
import { bank, lesson, m, MOE_REF, objective, opts, SAMPLE, type ObjectiveInput } from '../helpers';

export const subject: Subject = {
  id: 'science',
  name: { ar: 'الإيقاظ العلمي', fr: 'Éveil scientifique', en: 'Science' },
  glyph: '🌱',
  color: 'science',
  teachingLanguage: 'ar',
  levels: ['prep', 'y1', 'y2', 'y3', 'y4', 'y5', 'y6'],
  sources: MOE_REF,
  ...SAMPLE,
};

export const domains: LearningDomain[] = [
  {
    id: 'sci.living',
    subjectId: 'science',
    name: { ar: 'الكائنات الحيّة', fr: 'Le vivant', en: 'Living things' },
  },
  {
    id: 'sci.body',
    subjectId: 'science',
    name: { ar: 'جسم الإنسان والصحّة', fr: 'Le corps et la santé', en: 'Body and health' },
  },
  {
    id: 'sci.matter',
    subjectId: 'science',
    name: { ar: 'المادّة والطقس', fr: 'Matière et météo', en: 'Matter and weather' },
  },
  {
    id: 'sci.environment',
    subjectId: 'science',
    name: { ar: 'البيئة', fr: 'Environnement', en: 'Environment' },
  },
];

const o = (input: ObjectiveInput) => objective('science', 'ar', input);
const mc = 'misconception' as const;
const cl = 'classification' as const;

export const objectives = [
  o({
    id: 'sci.living-things',
    domainId: 'sci.living',
    levels: ['prep', 'y1'],
    order: 10,
    difficulty: 1,
    title: { ar: 'الحيوانات والنباتات', fr: 'Animaux et plantes', en: 'Animals and plants' },
    description: {
      ar: 'يميّز الحيوان من النبات ومن الأشياء غير الحيّة.',
      fr: 'Distinguer animaux, plantes et objets non vivants.',
      en: 'Tell animals, plants and non-living things apart.',
    },
    games: ['categorize', 'quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'sci.senses',
    domainId: 'sci.body',
    levels: ['y1'],
    order: 20,
    difficulty: 1,
    title: { ar: 'الحواسّ الخمس', fr: 'Les cinq sens', en: 'The five senses' },
    description: {
      ar: 'يربط كلّ عضو بالحاسّة المناسبة.',
      fr: 'Associer chaque organe à son sens.',
      en: 'Match each organ with its sense.',
    },
    games: ['memory', 'quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'sci.plant-parts',
    domainId: 'sci.living',
    levels: ['y2'],
    order: 30,
    difficulty: 2,
    prerequisites: ['sci.living-things'],
    title: { ar: 'أجزاء النبتة', fr: 'Les parties de la plante', en: 'Parts of a plant' },
    description: {
      ar: 'يتعرّف على أجزاء النبتة ودور كلّ جزء.',
      fr: 'Connaître les parties de la plante et leur rôle.',
      en: 'Know the parts of a plant and what they do.',
    },
    games: ['quick-quiz', 'find-correct', 'true-false', 'memory'],
  }),
  o({
    id: 'sci.seasons',
    domainId: 'sci.matter',
    levels: ['y2'],
    order: 40,
    difficulty: 2,
    title: { ar: 'الفصول والطقس', fr: 'Les saisons et la météo', en: 'Seasons and weather' },
    description: {
      ar: 'يتعرّف على الفصول الأربعة وما يناسب كلّ فصل.',
      fr: 'Connaître les quatre saisons.',
      en: 'Know the four seasons.',
    },
    games: ['categorize', 'quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'sci.healthy-food',
    domainId: 'sci.body',
    levels: ['y3'],
    order: 50,
    difficulty: 2,
    prerequisites: ['sci.senses'],
    title: { ar: 'تغذية متوازنة', fr: 'Une alimentation équilibrée', en: 'Balanced food' },
    description: {
      ar: 'يميّز الأغذية التي نتناولها بانتظام من تلك التي نقلّل منها.',
      fr: 'Distinguer les aliments à consommer souvent ou rarement.',
      en: 'Tell everyday foods from occasional ones.',
    },
    games: ['categorize', 'quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'sci.water-states',
    domainId: 'sci.matter',
    levels: ['y4'],
    order: 60,
    difficulty: 3,
    prerequisites: ['sci.seasons'],
    title: { ar: 'حالات الماء', fr: 'Les états de l’eau', en: 'States of water' },
    description: {
      ar: 'يتعرّف على حالات الماء الثلاث (الصلبة والسائلة والغازيّة) وعلى تحوّلاته.',
      fr: 'Connaître les états de l’eau et ses changements.',
      en: 'Know the states of water and how it changes.',
    },
    games: ['categorize', 'quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'sci.animal-diets',
    domainId: 'sci.living',
    levels: ['y5'],
    order: 70,
    difficulty: 3,
    prerequisites: ['sci.living-things'],
    title: { ar: 'تغذّي الحيوانات', fr: 'L’alimentation des animaux', en: 'What animals eat' },
    description: {
      ar: 'يصنّف الحيوانات إلى عاشبة ولاحمة وقارتة.',
      fr: 'Classer les animaux : herbivores, carnivores, omnivores.',
      en: 'Sort animals into herbivores, carnivores and omnivores.',
    },
    games: ['categorize', 'quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'sci.environment',
    domainId: 'sci.environment',
    levels: ['y6'],
    order: 80,
    difficulty: 3,
    title: {
      ar: 'أحافظ على البيئة',
      fr: 'Protéger l’environnement',
      en: 'Protecting the environment',
    },
    description: {
      ar: 'يميّز السلوكات التي تحمي البيئة من تلك التي تضرّها.',
      fr: 'Distinguer les gestes qui protègent l’environnement.',
      en: 'Tell actions that protect the environment from harmful ones.',
    },
    games: ['categorize', 'quick-quiz', 'find-correct', 'true-false'],
  }),
];

export const items = [
  ...bank('sci.living-things', 1, [
    {
      kind: 'categorize',
      prompt: 'صنّف: حيوان أم نبات؟',
      categories: [
        { id: 'animal', label: 'حيوان' },
        { id: 'plant', label: 'نبات' },
      ],
      entries: [
        { text: 'بقرة', emoji: '🐄', category: 'animal' },
        { text: 'شجرة', emoji: '🌳', category: 'plant' },
        { text: 'سمكة', emoji: '🐟', category: 'animal' },
        { text: 'عبّاد الشمس', emoji: '🌻', category: 'plant' },
        { text: 'عصفور', emoji: '🐦', category: 'animal' },
        { text: 'صبّار', emoji: '🌵', category: 'plant' },
        { text: 'نحلة', emoji: '🐝', category: 'animal' },
      ],
      mistake: cl,
    },
    {
      kind: 'choice',
      prompt: 'أيّ هذه نبات؟',
      ...opts({ text: 'شجرة', emoji: '🌳' }, m('قطّ', cl, '🐈'), m('كرة', cl, '⚽')),
    },
    {
      kind: 'choice',
      prompt: 'أيّ هذه حيوان؟',
      ...opts({ text: 'أرنب', emoji: '🐇' }, m('زهرة', cl, '🌷'), m('سيّارة', cl, '🚗')),
    },
    {
      kind: 'choice',
      prompt: 'إلى ماذا يحتاج كلّ كائن حيّ ليعيش؟',
      ...opts('الماء والغذاء', m('اللعب فقط', mc), m('الضوء الاصطناعي فقط', mc)),
    },
    { kind: 'trueFalse', statement: 'النباتات كائنات حيّة تنمو.', answer: true },
    {
      kind: 'trueFalse',
      statement: 'الحجر كائن حيّ.',
      answer: false,
      mistake: mc,
      explanation: 'الحجر لا يتغذّى ولا ينمو ولا يتكاثر، فهو ليس كائنًا حيًّا.',
    },
  ]),
  ...bank('sci.senses', 1, [
    {
      kind: 'match',
      prompt: 'اربط كلّ عضو بحاسّته.',
      pairs: [
        { left: 'العين', right: 'البصر', emoji: '👁️' },
        { left: 'الأذن', right: 'السمع', emoji: '👂' },
        { left: 'الأنف', right: 'الشمّ', emoji: '👃' },
        { left: 'اللسان', right: 'الذوق', emoji: '👅' },
        { left: 'الجلد', right: 'اللمس', emoji: '✋' },
      ],
      mistake: cl,
    },
    {
      kind: 'choice',
      prompt: 'بأيّ عضو نسمع الموسيقى؟',
      ...opts({ text: 'الأذن', emoji: '👂' }, m('العين', cl, '👁️'), m('الأنف', cl, '👃')),
    },
    {
      kind: 'choice',
      prompt: 'بأيّ حاسّة نعرف أنّ الحلوى حلوة؟',
      ...opts('الذوق', m('السمع', cl), m('البصر', cl)),
    },
    {
      kind: 'choice',
      prompt: 'بأيّ عضو نشمّ رائحة الوردة؟',
      ...opts({ text: 'الأنف', emoji: '👃' }, m('الأذن', cl, '👂'), m('اليد', cl, '✋')),
    },
    {
      kind: 'choice',
      prompt: 'نعرف أنّ الماء ساخن بحاسّة:',
      ...opts('اللمس', m('الذوق', cl), m('الشمّ', cl)),
    },
    { kind: 'trueFalse', statement: 'نرى الألوان بأعيننا.', answer: true },
    {
      kind: 'trueFalse',
      statement: 'نسمع بأنوفنا.',
      answer: false,
      mistake: mc,
      explanation: 'نسمع بآذاننا، ونشمّ بأنوفنا.',
    },
  ]),
  ...bank('sci.plant-parts', 2, [
    {
      kind: 'choice',
      prompt: 'أيّ جزء من النبتة يمتصّ الماء من التربة؟',
      ...opts('الجذور', m('الأوراق', mc), m('الزهرة', mc)),
    },
    {
      kind: 'choice',
      prompt: 'أيّ جزء يحمل الأوراق والأزهار؟',
      ...opts('الساق', m('الجذور', mc), m('البذرة', mc)),
    },
    {
      kind: 'choice',
      prompt: 'إلى ماذا تحتاج البذرة لتنبت؟',
      ...opts('الماء والهواء والحرارة المناسبة', m('الظلام فقط', mc), m('الملح', mc)),
    },
    {
      kind: 'choice',
      prompt: 'من أيّ جزء تتكوّن الثمرة؟',
      ...opts('من الزهرة', m('من الجذر', mc), m('من الساق', mc)),
    },
    { kind: 'trueFalse', statement: 'تحتاج النبتة إلى الضوء لتنمو.', answer: true },
    {
      kind: 'trueFalse',
      statement: 'تنمو جذور النبتة عادةً نحو الأعلى في الهواء.',
      answer: false,
      mistake: mc,
      explanation: 'تنمو الجذور عادةً داخل التربة نحو الأسفل.',
    },
    {
      kind: 'match',
      prompt: 'اربط كلّ جزء بدوره.',
      pairs: [
        { left: 'الجذور', right: 'تمتصّ الماء' },
        { left: 'الساق', right: 'تحمل الأوراق' },
        { left: 'الأوراق', right: 'تصنع غذاء النبتة' },
        { left: 'الزهرة', right: 'تعطي الثمرة' },
      ],
      mistake: mc,
    },
  ]),
  ...bank('sci.seasons', 2, [
    {
      kind: 'categorize',
      prompt: 'صنّف حسب الفصل:',
      categories: [
        { id: 'winter', label: 'الشتاء' },
        { id: 'summer', label: 'الصيف' },
      ],
      entries: [
        { text: 'مظلّة', emoji: '☂️', category: 'winter' },
        { text: 'وشاح', emoji: '🧣', category: 'winter' },
        { text: 'قفّازات', emoji: '🧤', category: 'winter' },
        { text: 'رجل الثلج', emoji: '⛄', category: 'winter' },
        { text: 'نظّارات شمسيّة', emoji: '🕶️', category: 'summer' },
        { text: 'السباحة في البحر', emoji: '🏖️', category: 'summer' },
        { text: 'مثلّجات', emoji: '🍦', category: 'summer' },
      ],
      mistake: cl,
    },
    {
      kind: 'choice',
      prompt: 'في أيّ فصل تتساقط أوراق كثير من الأشجار؟',
      ...opts('الخريف', m('الربيع', mc), m('الصيف', mc)),
    },
    {
      kind: 'choice',
      prompt: 'في أيّ فصل تتفتّح الأزهار بكثرة؟',
      ...opts('الربيع', m('الخريف', mc), m('الشتاء', mc)),
    },
    { kind: 'choice', prompt: 'كم عدد فصول السنة؟', ...opts('4', m('3', mc), m('12', mc)) },
    { kind: 'trueFalse', statement: 'الشتاء أبرد فصول السنة في تونس.', answer: true },
    {
      kind: 'trueFalse',
      statement: 'يأتي الصيف مباشرةً بعد الشتاء.',
      answer: false,
      mistake: mc,
      explanation: 'بعد الشتاء يأتي الربيع، ثمّ الصيف.',
    },
  ]),
  ...bank('sci.healthy-food', 2, [
    {
      kind: 'categorize',
      prompt: 'صنّف الأغذية:',
      categories: [
        { id: 'often', label: 'نتناوله بانتظام' },
        { id: 'rarely', label: 'نقلّل منه' },
      ],
      entries: [
        { text: 'الغلال', emoji: '🍎', category: 'often' },
        { text: 'الخضر', emoji: '🥕', category: 'often' },
        { text: 'الحليب', emoji: '🥛', category: 'often' },
        { text: 'الماء', emoji: '💧', category: 'often' },
        { text: 'الحلوى', emoji: '🍬', category: 'rarely' },
        { text: 'المشروبات الغازيّة', emoji: '🥤', category: 'rarely' },
        { text: 'الأطعمة المقليّة', emoji: '🍟', category: 'rarely' },
      ],
      mistake: cl,
    },
    {
      kind: 'choice',
      prompt: 'أيّ فطور أكثر توازنًا؟',
      ...opts('حليب وخبز وغلال', m('مشروب غازي وحلوى', mc), m('بطاطا مقليّة فقط', mc)),
    },
    {
      kind: 'choice',
      prompt: 'لماذا نغسل أيدينا قبل الأكل؟',
      ...opts('لنزيل الجراثيم', m('ليصبح الطعام أحلى', mc), m('لنأكل أسرع', mc)),
    },
    {
      kind: 'choice',
      prompt: 'ما أفضل مشروب للجسم؟',
      ...opts('الماء', m('المشروب الغازي', mc), m('العصير المحلّى', mc)),
    },
    { kind: 'trueFalse', statement: 'تحتوي الخضر والغلال على فيتامينات مفيدة.', answer: true },
    {
      kind: 'trueFalse',
      statement: 'يمكن أن نأكل الحلوى بدل وجبة الغداء كلّ يوم.',
      answer: false,
      mistake: mc,
      explanation: 'الحلوى لا تعطي الجسم كلّ ما يحتاج إليه، فنأكلها أحيانًا فقط.',
    },
  ]),
  ...bank('sci.water-states', 3, [
    {
      kind: 'categorize',
      prompt: 'صنّف حسب حالة الماء:',
      categories: [
        { id: 'solid', label: 'صلب' },
        { id: 'liquid', label: 'سائل' },
        { id: 'gas', label: 'غازي' },
      ],
      entries: [
        { text: 'الجليد', emoji: '🧊', category: 'solid' },
        { text: 'الثلج', emoji: '❄️', category: 'solid' },
        { text: 'ماء الحنفيّة', emoji: '🚰', category: 'liquid' },
        { text: 'قطرات المطر', emoji: '🌧️', category: 'liquid' },
        { text: 'ماء البحر', emoji: '🌊', category: 'liquid' },
        { text: 'بخار الماء', emoji: '♨️', category: 'gas' },
      ],
      mistake: cl,
    },
    {
      kind: 'choice',
      prompt: 'ماذا يحدث للجليد إذا تركناه في الشمس؟',
      ...opts('ينصهر ويصبح ماءً سائلًا', m('يصبح أكثر صلابة', mc), m('يتحوّل إلى ملح', mc)),
    },
    {
      kind: 'choice',
      prompt: 'ماذا نسمّي تحوّل الماء إلى بخار؟',
      ...opts('التبخّر', m('الانصهار', mc), m('التجمّد', mc)),
    },
    {
      kind: 'choice',
      prompt: 'ماذا نسمّي تحوّل الماء إلى جليد؟',
      ...opts('التجمّد', m('التبخّر', mc), m('الانصهار', mc)),
    },
    {
      kind: 'choice',
      prompt: 'عند أيّ درجة حرارة يغلي الماء النقيّ تقريبًا؟',
      ...opts('100 درجة', m('10 درجات', mc), m('0 درجة', mc)),
    },
    { kind: 'trueFalse', statement: 'يتبخّر الماء أسرع عند تسخينه.', answer: true },
    {
      kind: 'trueFalse',
      statement: 'الجليد ماء في حالة غازيّة.',
      answer: false,
      mistake: mc,
      explanation: 'الجليد ماء في حالة صلبة.',
    },
  ]),
  ...bank('sci.animal-diets', 3, [
    {
      kind: 'categorize',
      prompt: 'صنّف الحيوانات حسب غذائها:',
      categories: [
        { id: 'herbivore', label: 'عاشب' },
        { id: 'carnivore', label: 'لاحم' },
        { id: 'omnivore', label: 'قارت' },
      ],
      entries: [
        { text: 'البقرة', emoji: '🐄', category: 'herbivore' },
        { text: 'الخروف', emoji: '🐑', category: 'herbivore' },
        { text: 'الأرنب', emoji: '🐇', category: 'herbivore' },
        { text: 'الأسد', emoji: '🦁', category: 'carnivore' },
        { text: 'الذئب', emoji: '🐺', category: 'carnivore' },
        { text: 'النسر', emoji: '🦅', category: 'carnivore' },
        { text: 'الدجاجة', emoji: '🐔', category: 'omnivore' },
        { text: 'الدبّ', emoji: '🐻', category: 'omnivore' },
      ],
      mistake: cl,
    },
    {
      kind: 'choice',
      prompt: 'ماذا نسمّي الحيوان الذي يتغذّى بالنباتات فقط؟',
      ...opts('عاشب', m('لاحم', cl), m('قارت', cl)),
    },
    { kind: 'choice', prompt: 'الأسد حيوان:', ...opts('لاحم', m('عاشب', cl), m('قارت', cl)) },
    {
      kind: 'choice',
      prompt: 'ماذا يأكل الحيوان القارت؟',
      ...opts('النباتات واللحوم', m('اللحوم فقط', cl), m('النباتات فقط', cl)),
    },
    { kind: 'trueFalse', statement: 'للحيوانات اللاحمة أنياب حادّة.', answer: true },
    {
      kind: 'trueFalse',
      statement: 'الخروف حيوان لاحم.',
      answer: false,
      mistake: cl,
      explanation: 'الخروف يأكل العشب، فهو حيوان عاشب.',
    },
  ]),
  ...bank('sci.environment', 3, [
    {
      kind: 'categorize',
      prompt: 'صنّف السلوكات:',
      categories: [
        { id: 'protects', label: 'يحمي البيئة' },
        { id: 'harms', label: 'يضرّ البيئة' },
      ],
      entries: [
        { text: 'زراعة شجرة', emoji: '🌳', category: 'protects' },
        { text: 'إطفاء الضوء عند الخروج', emoji: '💡', category: 'protects' },
        { text: 'فرز الفضلات', emoji: '♻️', category: 'protects' },
        { text: 'رمي الأكياس في الطبيعة', emoji: '🛍️', category: 'harms' },
        { text: 'ترك الحنفيّة مفتوحة', emoji: '🚰', category: 'harms' },
        { text: 'حرق الفضلات', emoji: '🔥', category: 'harms' },
      ],
      mistake: cl,
    },
    {
      kind: 'choice',
      prompt: 'كيف نقتصد في الماء؟',
      ...opts(
        'نغلق الحنفيّة أثناء تنظيف الأسنان',
        m('نترك الحنفيّة مفتوحة', mc),
        m('نغسل السيّارة كلّ يوم', mc),
      ),
    },
    {
      kind: 'choice',
      prompt: 'أين نضع القارورة البلاستيكيّة الفارغة؟',
      ...opts('في حاوية إعادة التدوير', m('في البحر', mc), m('في الحديقة', mc)),
    },
    {
      kind: 'choice',
      prompt: 'ما الذي يلوّث الهواء؟',
      ...opts('دخان السيّارات والمصانع', m('الأشجار', mc), m('المطر', mc)),
    },
    { kind: 'trueFalse', statement: 'يمكن إعادة تدوير الورق والبلاستيك.', answer: true },
    {
      kind: 'trueFalse',
      statement: 'رمي الفضلات في البحر لا يضرّ الأسماك.',
      answer: false,
      mistake: mc,
      explanation: 'الفضلات تلوّث البحر وقد تأكلها الأسماك فتمرض.',
    },
  ]),
];

export const lessons = [
  lesson('sci.living-things', 'ما هو الكائن الحيّ؟', [
    { type: 'intro', text: 'الكائن الحيّ يتغذّى وينمو ويتكاثر، مثل الحيوانات والنباتات.' },
    {
      type: 'illustration',
      figure: {
        type: 'cards',
        cards: [
          { emoji: '🐄', title: 'حيوان', text: 'يتغذّى وينمو ويتحرّك' },
          { emoji: '🌳', title: 'نبات', text: 'يتغذّى وينمو' },
          { emoji: '⚽', title: 'شيء غير حيّ', text: 'لا يتغذّى ولا ينمو' },
        ],
      },
    },
    {
      type: 'example',
      text: 'الشجرة تنمو فهي كائن حيّ، أمّا الكرة فلا تنمو.',
      visual: { emoji: '🌳 ⚽', label: 'شجرة وكرة' },
    },
    {
      type: 'rule',
      title: 'علامات الكائن الحيّ',
      text: 'الكائن الحيّ يتنفّس ويتغذّى وينمو ويتكاثر. إذا لم يفعل ذلك فهو شيء غير حيّ.',
      examples: [
        { text: '🐟 السمكة تتنفّس وتتغذّى وتنمو ← كائن حيّ.' },
        { text: '🌻 عبّاد الشمس ينمو ويعطي بذورًا ← كائن حيّ.' },
        { text: '🪨 الحجر لا يتغذّى ولا ينمو ← شيء غير حيّ.' },
      ],
    },
    { type: 'tip', text: 'اسأل: هل يتغذّى؟ هل ينمو؟' },
  ]),
  lesson('sci.senses', 'حواسّي الخمس', [
    { type: 'intro', text: 'نتعرّف على العالم من حولنا بحواسّنا الخمس.' },
    {
      type: 'illustration',
      figure: {
        type: 'cards',
        cards: [
          { emoji: '👁️', title: 'البصر', text: 'العين' },
          { emoji: '👂', title: 'السمع', text: 'الأذن' },
          { emoji: '👃', title: 'الشمّ', text: 'الأنف' },
          { emoji: '👅', title: 'الذوق', text: 'اللسان' },
          { emoji: '✋', title: 'اللمس', text: 'الجلد' },
        ],
      },
    },
    {
      type: 'example',
      text: 'نرى بالعين، ونسمع بالأذن، ونشمّ بالأنف، ونتذوّق باللسان، ونلمس بالجلد.',
      visual: { emoji: '👁️ 👂 👃 👅 ✋', label: 'الحواسّ' },
    },
    { type: 'tip', text: 'جرّب: أغمض عينيك واستمع. أيّ حاسّة تستعمل؟' },
  ]),
  lesson('sci.plant-parts', 'أجزاء النبتة', [
    { type: 'intro', text: 'للنبتة جذور وساق وأوراق، وقد تكون لها أزهار وثمار.' },
    {
      type: 'illustration',
      figure: {
        type: 'plantParts',
        labels: { flower: 'الزهرة', leaves: 'الأوراق', stem: 'الساق', roots: 'الجذور' },
      },
    },
    {
      type: 'example',
      text: 'الجذور تمتصّ الماء، والساق تحمل الأوراق، والزهرة تعطي الثمرة.',
      visual: { emoji: '🌱', label: 'نبتة' },
    },
    { type: 'tip', text: 'جرّب أن تزرع بذرة فاصوليا في قطن مبلّل وراقبها كلّ يوم.' },
  ]),
  lesson('sci.seasons', 'فصول السنة', [
    { type: 'intro', text: 'للسنة أربعة فصول: الخريف، والشتاء، والربيع، والصيف.' },
    {
      type: 'illustration',
      figure: {
        type: 'cards',
        cards: [
          { emoji: '🍂', title: 'الخريف' },
          { emoji: '❄️', title: 'الشتاء' },
          { emoji: '🌸', title: 'الربيع' },
          { emoji: '☀️', title: 'الصيف' },
        ],
      },
      caption: 'تتعاقب الفصول بهذا الترتيب كلّ سنة.',
    },
    {
      type: 'example',
      text: 'في الشتاء نلبس ملابس دافئة، وفي الصيف نحتمي من الشمس.',
      visual: { emoji: '🍂 ❄️ 🌸 ☀️', label: 'الفصول' },
    },
    { type: 'tip', text: 'لاحظ الطقس من نافذتك وسجّله في كرّاسك.' },
  ]),
  lesson('sci.healthy-food', 'غذائي المتوازن', [
    { type: 'intro', text: 'الغذاء المتوازن يعطي الجسم ما يحتاج إليه لينمو ويلعب ويتعلّم.' },
    {
      type: 'illustration',
      figure: {
        type: 'cards',
        cards: [
          { emoji: '🍎', title: 'كلّ يوم', text: 'غلال وخضر وماء' },
          { emoji: '🍬', title: 'أحيانًا فقط', text: 'حلوى ومشروبات غازيّة' },
        ],
      },
    },
    {
      type: 'example',
      text: 'فطور متوازن: حليب وخبز وغلال.',
      visual: { emoji: '🥛 🍞 🍎', label: 'فطور' },
    },
    { type: 'tip', text: 'الماء أفضل مشروب، والحلوى نأكلها أحيانًا فقط.' },
  ]),
  lesson('sci.water-states', 'الماء يتغيّر', [
    {
      type: 'intro',
      text: 'يوجد الماء في ثلاث حالات: صلبة (الجليد) وسائلة (الماء) وغازيّة (البخار).',
    },
    {
      type: 'example',
      text: 'إذا سخّنّا الجليد ينصهر، وإذا سخّنّا الماء يتبخّر.',
      visual: { emoji: '🧊 → 💧 → ♨️', label: 'حالات الماء' },
    },
    {
      type: 'rule',
      title: 'الحرارة تغيّر حالة الماء',
      text: 'إذا سخّنّا الجليد ينصهر فيصبح ماءً، وإذا سخّنّا الماء يتبخّر. وإذا برّدنا الماء كثيرًا يتجمّد.',
      figure: {
        type: 'sequence',
        steps: [
          { emoji: '🧊', label: 'جليد' },
          { emoji: '💧', label: 'ماء' },
          { emoji: '♨️', label: 'بخار' },
        ],
        arrows: ['انصهار', 'تبخّر'],
      },
      examples: [
        { text: '🍦 المثلّجات تذوب في الشمس: انصهار.' },
        { text: '👕 الثياب المبلّلة تجفّ تحت الشمس: تبخّر.' },
        { text: '❄️ نضع الماء في المجمّدة فيصبح جليدًا: تجمّد.' },
      ],
    },
    { type: 'tip', text: 'جرّب بمساعدة شخص كبير: ضع مكعّب جليد في صحن ولاحظ ما يحدث.' },
  ]),
  lesson('sci.animal-diets', 'ماذا تأكل الحيوانات؟', [
    {
      type: 'intro',
      text: 'الحيوان العاشب يأكل النباتات، واللاحم يأكل اللحوم، والقارت يأكل الاثنين.',
    },
    {
      type: 'example',
      text: 'البقرة عاشبة، والأسد لاحم، والدجاجة قارتة.',
      visual: { emoji: '🐄 🦁 🐔', label: 'حيوانات' },
    },
    { type: 'tip', text: 'انظر إلى أسنان الحيوان: الأنياب الحادّة تساعد على أكل اللحم.' },
  ]),
  lesson('sci.environment', 'بيئتي أمانة', [
    { type: 'intro', text: 'البيئة هي كلّ ما يحيط بنا من ماء وهواء وتربة وكائنات حيّة.' },
    {
      type: 'illustration',
      figure: {
        type: 'cards',
        cards: [
          { emoji: '🐄', title: 'عاشب', text: 'يأكل النباتات' },
          { emoji: '🦁', title: 'لاحم', text: 'يأكل اللحوم' },
          { emoji: '🐔', title: 'قارت', text: 'يأكل الاثنين' },
        ],
      },
    },
    {
      type: 'illustration',
      figure: {
        type: 'sequence',
        steps: [
          { emoji: '🧊', label: 'صلب' },
          { emoji: '💧', label: 'سائل' },
          { emoji: '♨️', label: 'غازي' },
        ],
        arrows: ['الانصهار', 'التبخّر'],
      },
      caption: 'كلّما سخّنّا الماء تغيّرت حالته.',
    },
    {
      type: 'example',
      text: 'نفرز الفضلات، ونقتصد في الماء، ونزرع الأشجار.',
      visual: { emoji: '♻️ 💧 🌳', label: 'حماية البيئة' },
    },
    { type: 'tip', text: 'كلّ عمل صغير يساعد: أغلق الحنفيّة جيّدًا.' },
  ]),
];
